import { Box, makeStyles, LabelValue, Typography } from '@dxb-web-ui-framework/design-system';
import React, { FC } from 'react';
import { TFunction } from 'react-i18next';
import { Nullable } from '@dxb-web-ui-framework/common/types';
import { useScreenDetector } from '@dxb-web-ui-framework/common/hooks';
import { getClientTimezone, stringToDate } from '@dxb-web-ui-framework/common/utils/Date';
import { PayrollDetails } from '../../domain/Payroll/entities/types';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(3),
    },
  },
  boxContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    border: `2px solid ${theme.palette.background.default}`,
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      flex: 1,
      backgroundColor: '#F0F0F0',
    },
  },
  labelValueWrapper: {
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      paddingTop: theme.spacing(0),
    },
  },
}));

export interface ModificationsProps {
  t: TFunction;
  payrollDetails: Nullable<PayrollDetails>;
  isLoading: boolean;
}

const Modifications: FC<ModificationsProps> = ({ t, payrollDetails, isLoading }) => {
  const classes = useStyles();
  const { isExtraSmallScreen: isMobile } = useScreenDetector();
  const locale = t('global.settings.date.locale');
  const dateFormat = t('global.settings.date.format');
  const timezone = getClientTimezone();

  return (
    <Box className={classes.root}>
      <Typography variant={'subtitle2'} color={'textPrimary'}>
        {t('payrollDetailsContent.modifications')}
      </Typography>
      <Box p={2} className={classes.boxContainer}>
        <Box className={classes.labelValueWrapper}>
          <LabelValue
            label={t('payrollDetails.insertedBy')}
            value={payrollDetails?.insertedBy}
            valueAreaLabel={`${t('payrollDetails.insertedBy')} ${payrollDetails?.insertedBy}`}
            labelColor='textPrimary'
            labelSize='small'
            valueSize={isMobile ? 'small' : 'medium'}
            valueColor='primary'
            labelAlignment='left'
            valueAlignment='left'
            hasSkeleton={isLoading}
          />
        </Box>
        {payrollDetails?.insertedDate && (
          <Box className={classes.labelValueWrapper}>
            <LabelValue
              label={t('payrollDetails.insertedDate')}
              value={stringToDate({
                date: payrollDetails?.insertedDate,
                formatExp: dateFormat,
                localeName: locale,
                tz: timezone,
              })}
              valueAreaLabel={`${t('payrollDetails.insertedDate')} ${stringToDate({
                date: payrollDetails?.insertedDate,
                formatExp: dateFormat,
                localeName: locale,
                tz: timezone,
              })}`}
              labelColor='textPrimary'
              labelSize='small'
              valueSize={isMobile ? 'small' : 'medium'}
              valueColor='primary'
              labelAlignment='left'
              valueAlignment='left'
              hasSkeleton={isLoading}
            />
          </Box>
        )}
        <Box className={classes.labelValueWrapper}>
          <LabelValue
            label={t('payrollDetails.updatedBy')}
            value={payrollDetails?.updatedBy}
            valueAreaLabel={`${t('payrollDetails.updatedBy')} ${payrollDetails?.updatedBy}`}
            labelColor='textPrimary'
            labelSize='small'
            valueSize={isMobile ? 'small' : 'medium'}
            valueColor='primary'
            labelAlignment='left'
            valueAlignment='left'
            hasSkeleton={isLoading}
          />
        </Box>
        {payrollDetails?.updatedDate && (
          <Box className={classes.labelValueWrapper}>
            <LabelValue
              label={t('payrollDetails.updatedDate')}
              value={stringToDate({
                date: payrollDetails?.updatedDate,
                formatExp: dateFormat,
                localeName: locale,
                tz: timezone,
              })}
              valueAreaLabel={`${t('payrollDetails.updatedDate')} ${stringToDate({
                date: payrollDetails?.updatedDate,
                formatExp: dateFormat,
                localeName: locale,
                tz: timezone,
              })}`}
              labelColor='textPrimary'
              labelSize='small'
              valueSize={isMobile ? 'small' : 'medium'}
              valueColor='primary'
              labelAlignment='left'
              valueAlignment='left'
              hasSkeleton={isLoading}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export { Modifications };
